/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ProductType, ProductWarehouse, ReleasedProductType, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import {
    getSelectedProductIdFromActionInput,
    getSelectedVariant,
    IProductInventoryInformation,
    SelectedVariantInput,
    mapAggregatedProductInventoryInformation,
    DeliveryMode
} from '@msdyn365-commerce-modules/retail-actions';
//import { getCartState } from '@msdyn365-commerce/global-state';
//import { getAttributeTextValueByName } from '../../../utilities/attributes-helpers';
import { mttGetZoneWarehousesAsync } from '../../../proxy/DataActionExtension.g';
//import { MTT_SELECTED_COUNTY_ID_TRANSACTION_ATTRIBUTE } from '../../../constants';
import { WarehouseClass } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceModels.g';
import { getEstimatedProductWarehouseAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { filterWarehousesByType, getWarehouseTypeForUnitOfMeasure } from '../../../utilities/warehouse-helpers';

/**
 * Input class for the getProductAvailabilitiesForSelectedVariant Data Action.
 */
export class ProductWarehouseAvailabilitiesForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public countyId?: string;

    public unitOfMeasure?: string;

    public selectedProduct: SimpleProduct | undefined;

    public constructor(
        productId: number | string,
        channelId: number,
        selectedProduct?: SimpleProduct,
        countyId?: string,
        unitOfMeasure?: string
    ) {
        this.productId = typeof productId === 'string' ? +productId : productId;
        this.channelId = channelId;
        this.selectedProduct = selectedProduct;
        this.countyId = countyId;
        this.unitOfMeasure = unitOfMeasure;
    }

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => 'ProductWarehouseAvailabilitiesForSelectedVariant';

    /**
     * GetCacheObjectType.
     * @returns - Cache Object Type string.
     */
    public getCacheObjectType = (): string => 'ProductAvailabilities';

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the getProductAvailabilitiesForSelectedVariant data action.
 * @param inputData - The input data passed to the createInput method.
 * @returns - ProductAvailabilitiesForSelectedVariant or error.
 */
export const createProductWarehouseAvailabilitiesForSelectedVariantInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): ProductWarehouseAvailabilitiesForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new ProductWarehouseAvailabilitiesForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId);
    }
    throw new Error('Unable to create ProductWarehouseAvailabilitiesForSelectedVariantInput, no productId found on module config or query');
};

/**
 * The action method for the getProductAvailabilitiesForSelectedVariant data action.
 * @param input - ProductAvailabilitiesForSelectedVariant.
 * @param ctx - Action Context.
 * @returns - Product inventory information.
 */
export async function getProductWarehouseAvailabilitiesForSelectedVariantAction(
    input: ProductWarehouseAvailabilitiesForSelectedVariantInput,
    ctx: IActionContext
): Promise<IProductInventoryInformation[] | undefined> {
    try {
        let productResult: SimpleProduct | null = input.selectedProduct ?? null;

        if (!productResult) {
            const selectedVariantInput = new SelectedVariantInput(
                input.productId,
                input.channelId,
                undefined,
                undefined,
                ctx.requestContext
            );

            productResult = await getSelectedVariant(selectedVariantInput, ctx);
        }

        // Don't get availability if it is service item && enableStockCheck is disbaled.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Reading config.
        if (
            ctx.requestContext.app.config.enableStockCheck &&
            productResult &&
            productResult.ItemTypeValue !== ReleasedProductType.Service &&
            productResult.ProductTypeValue !== ProductType.Master
        ) {
            const countyId: string | null = input.countyId ?? null;
            if (!countyId) {
                /*
                const cartState = await getCartState(ctx);
                if (cartState?.cart) {
                    countyId =
                        getAttributeTextValueByName(cartState.cart.AttributeValues!, MTT_SELECTED_COUNTY_ID_TRANSACTION_ATTRIBUTE) ?? '';
                }
                */
            }

            let warehouses: WarehouseClass[] = [];
            if (countyId) {
                warehouses = await mttGetZoneWarehousesAsync({ callerContext: ctx }, countyId ?? '');
            }

            if (ctx.requestContext?.app?.config?.enableWarehouseTypeFulfilment === true) {
                warehouses = filterWarehousesByType(
                    warehouses,
                    getWarehouseTypeForUnitOfMeasure(
                        // if unit of measure is not provided, we should consider default unit of measure
                        input?.unitOfMeasure ?? productResult?.DefaultUnitOfMeasure,
                        productResult?.DefaultUnitOfMeasure
                    )
                );
            }

            let productAvailabilities: IProductInventoryInformation[] = [];
            if (productResult && warehouses.length) {
                const productWarehouses: ProductWarehouse[] = [];

                for (const warehouse of warehouses) {
                    const productWarehouse: ProductWarehouse = {
                        ProductId: productResult.RecordId,
                        InventLocationId: warehouse.InventLocation
                    };

                    if (input?.unitOfMeasure) {
                        productWarehouse.UnitOfMeasure = input.unitOfMeasure;
                    }

                    if (ctx.requestContext.channel && ctx.requestContext.channel.InventLocationDataAreaId) {
                        productWarehouse.DataAreaId = ctx.requestContext.channel.InventLocationDataAreaId;
                    }
                    productWarehouses.push(productWarehouse);
                }

                const quantityUnitTypeSales: number = 2;
                const getProductWarehouseAvail = await getEstimatedProductWarehouseAvailabilityAsync(
                    { callerContext: ctx, bypassCache: 'get', queryResultSettings: {} },
                    productWarehouses,
                    quantityUnitTypeSales
                );

                if (
                    getProductWarehouseAvail &&
                    getProductWarehouseAvail.ProductWarehouseInventoryAvailabilities &&
                    getProductWarehouseAvail.ProductWarehouseInventoryAvailabilities.length > 0
                ) {
                    const productWarehouseMapping = mapAggregatedProductInventoryInformation(ctx, getProductWarehouseAvail);
                    if (productWarehouseMapping && productWarehouseMapping.length > 0) {
                        for (const item of productWarehouseMapping) {
                            item.deliveryType = DeliveryMode.shipping;
                            productAvailabilities.push(item);
                        }
                    }
                }
            }

            if (productAvailabilities?.length) {
                productAvailabilities = productAvailabilities
                    .filter(availability => availability.IsProductAvailable)
                    .sort(
                        (a, b) =>
                            (b.ProductAvailableQuantity?.AvailableQuantity ?? 0) - (a.ProductAvailableQuantity?.AvailableQuantity ?? 0)
                    );
            }

            return productAvailabilities;
        }
        return undefined;
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- cann't assign type to the error variable.
        ctx.telemetry.exception(error);
        ctx.telemetry.debug('[getPriceForSelectedVariantAction]Error executing action');
        return undefined;
    }
}

/**
 * The complete getProductAvailabilitiesForSelectedVariant data action
 * Get the currently selected variant via the getSelectedVariant data action, and
 * then gets the availabilities for the variant via the getProductAvailabilities RetailServer API.
 */
export const getProductAvailabilitiesForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-warehouse-availabilities-for-selected-variant',
    action: <IAction<IProductInventoryInformation[] | null>>getProductWarehouseAvailabilitiesForSelectedVariantAction,
    input: createProductWarehouseAvailabilitiesForSelectedVariantInput
});

export default getProductAvailabilitiesForSelectedVariantActionDataAction;
